import {
  CreateSupportParams,
  CreateSupportResponse,
  DocumentTransaction,
} from "@/app/services/supportService/createSupport";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { SupportCategoryEnum } from "@/app/utils/supportCategoryEnum";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import {
  MergedSupportData,
  prepareSupportData,
} from "./components/prepareSupportData";
import { useGetAllSupports } from "@/app/hooks/useGetAllSupports";
import { useNavigate } from "react-router-dom";
import { supportService } from "@/app/services/supportService";
import { useDebounce } from "@/app/hooks/useDebounce";

export const useSupportController = () => {
  const [selectedFilters, setSelectedFilters] = useState<SupportCategoryEnum[]>(
    [SupportCategoryEnum.ALL],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [createSupportResponse, setCreateSupportResponse] = useState<
    CreateSupportResponse | undefined
  >(undefined);
  const [selectedId, setSelectedId] = useAtom(
    selectedIdfromTableRowTransaction,
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const queryClient = useQueryClient();
  const preparedData = prepareSupportData(createSupportResponse);
  const debouncedSearchValue = useDebounce(1000, searchQuery);

  const { data, isFetching, isLoading } = useGetAllSupports();

  const navigate = useNavigate();

  const [documentsTransactions, setDocumentsTransactions] = useState<
    DocumentTransaction[]
  >([]);

  const { data: documentsData, refetch: refetchDocuments } = useQuery({
    queryKey: ["supportDocuments"],
    queryFn: () =>
      supportService.createSupport({
        search: "",
        categories: [],
        documents: true,
      }),
  });

  useEffect(() => {
    if (documentsData?.documentsTransactions) {
      setDocumentsTransactions(documentsData.documentsTransactions);
    }
  }, [documentsData]);

  const handleRowClick = async (item: MergedSupportData) => {
    setSelectedId(item._id);

    if (item.type === SupportCategoryEnum.TRANSACTION) {
      setSheetOpen(true);
    } else if (item.type === SupportCategoryEnum.USER) {
      navigate(`/vendedores/${item._id}`);
    } else if (item.type === SupportCategoryEnum.CUSTOMER) {
      navigate(`/clientes/${item._id}`);
    }
  };

  const handleDocumentRowClick = (item: DocumentTransaction) => {
    setSelectedId(item._id);
    setSheetOpen(true);
  };

  const handleSheetState = () => {
    setSheetOpen(!sheetOpen);
  };

  const { mutateAsync } = useMutation({
    mutationFn: (data: CreateSupportParams) =>
      supportService.createSupport(data),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["getAllSupports"],
      });
    },
  });

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFetch = useCallback(async () => {
    let filters = selectedFilters;
    if (selectedFilters.includes(SupportCategoryEnum.ALL)) {
      filters = [
        SupportCategoryEnum.CUSTOMER,
        SupportCategoryEnum.USER,
        SupportCategoryEnum.TRANSACTION,
      ];
    }

    const response = await mutateAsync({
      categories: filters,
      search: searchQuery,
      documents: true,
    });

    if (response) {
      setCreateSupportResponse(response);
      if (response.documentsTransactions) {
        setDocumentsTransactions(response.documentsTransactions);
      }
    }
  }, [mutateAsync, searchQuery, selectedFilters]);

  const handleMakeFetchAgain = (
    itemSearch: string,
    itemCategories: SupportCategoryEnum[],
  ) => {
    setSearchQuery(itemSearch);
    setSelectedFilters(itemCategories);
  };

  useEffect(() => {
    if (
      debouncedSearchValue &&
      debouncedSearchValue === searchQuery &&
      selectedFilters.length > 0
    ) {
      handleFetch();
    } else if (!debouncedSearchValue) {
      setCreateSupportResponse(undefined);
      if (documentsData?.documentsTransactions) {
        setDocumentsTransactions(documentsData.documentsTransactions);
      }
      refetchDocuments();
    }
  }, [
    handleFetch,
    debouncedSearchValue,
    selectedFilters,
    searchQuery,
    refetchDocuments,
  ]);

  return {
    selectedId,
    data,
    isLoading,
    isFetching,
    handleRowClick,
    handleDocumentRowClick,
    handleSheetState,
    handleSearch,
    handleMakeFetchAgain,
    selectedFilters,
    setSelectedFilters,
    searchQuery,
    preparedData,
    documentsTransactions,
    sheetOpen,
    setSheetOpen,
  };
};
